import React, { useCallback, useEffect, useRef, useState } from 'react';
import profileApi from '../services/profileApi';
import Icon from './Icon';
import ProfilePdfComparison from './ProfilePdfComparison';
import './ProfilePdfUpload.css';

const formatDate = (iso) => {
  const date = iso ? new Date(iso) : null;
  return date && !Number.isNaN(date.getTime())
    ? date.toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' })
    : '';
};

/**
 * Check a LinkedIn profile PDF (More > Save to PDF on LinkedIn) against one person, then save
 * what it says about them. onSaved receives the person as now on file.
 */
const ProfilePdfUpload = ({ contactId, contactName, onSaved }) => {
  const inputRef = useRef(null);
  // The unsaved upload's import_id, dropped if the person changes or the dialog closes
  const pendingRef = useRef({ id: null });
  const [phase, setPhase] = useState('idle'); // idle | reading | review | saving
  const [upload, setUpload] = useState(null);
  const [review, setReview] = useState(null);
  const [confirmed, setConfirmed] = useState(false);
  const [error, setError] = useState(null);
  const [notice, setNotice] = useState(null);
  const [history, setHistory] = useState([]);

  const loadHistory = useCallback(() => {
    if (!contactId) return;
    profileApi.getContactHistory(contactId)
      .then(setHistory)
      .catch(() => setHistory([]));
  }, [contactId]);

  useEffect(() => {
    setPhase('idle');
    setUpload(null);
    setReview(null);
    setConfirmed(false);
    setError(null);
    setNotice(null);
    setHistory([]);
    loadHistory();
    const pending = pendingRef.current;
    return () => {
      if (pending.id) profileApi.discardImport(pending.id);
      pending.id = null;
    };
  }, [contactId, loadHistory]);

  const readFile = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;
    setError(null);
    setNotice(null);
    setPhase('reading');
    try {
      const result = await profileApi.uploadContactPdf(contactId, file);
      pendingRef.current.id = result.import_id;
      setUpload(result);
      setReview(result);
      setConfirmed(false);
      setPhase('review');
    } catch (err) {
      setError(err.message);
      setPhase('idle');
    }
  };

  const cancel = () => {
    if (pendingRef.current.id) profileApi.discardImport(pendingRef.current.id);
    pendingRef.current.id = null;
    setUpload(null);
    setReview(null);
    setError(null);
    setPhase('idle');
  };

  const save = async () => {
    setPhase('saving');
    setError(null);
    try {
      const result = await profileApi.saveContactPdf(contactId, upload.import_id, review.plan_hash, confirmed);
      pendingRef.current.id = null;
      const count = result.saved.changes;
      setNotice(`Saved ${count} change${count === 1 ? '' : 's'} from ${upload.file_name || 'the PDF'}.`);
      setUpload(null);
      setReview(null);
      setPhase('idle');
      loadHistory();
      if (onSaved) onSaved(result.contact);
    } catch (err) {
      // The person changed, or the PDF needs confirming: show the fresh comparison to check again
      if (err.review) {
        setReview(err.review);
        setConfirmed(false);
      }
      setError(err.message);
      setPhase('review');
    }
  };

  const latest = history[0];
  const canSave = review && review.changes?.length > 0 && (!review.needs_confirmation || confirmed);

  return (
    <section className="pdf-upload" aria-label="LinkedIn profile PDF">
      <input
        ref={inputRef}
        type="file"
        accept=".pdf,application/pdf"
        className="pdf-upload__input"
        onChange={readFile}
        tabIndex={-1}
        aria-hidden="true"
      />

      {(phase === 'idle' || phase === 'reading') && (
        <div className="pdf-upload__start">
          <button
            type="button"
            className="pdf-upload__button"
            onClick={() => inputRef.current?.click()}
            disabled={phase === 'reading'}
          >
            <Icon name={phase === 'reading' ? 'loader' : 'upload'} spin={phase === 'reading'} />
            {phase === 'reading' ? 'Reading the PDF…' : 'Check against a LinkedIn PDF'}
          </button>
          <span className="pdf-upload__hint">
            {latest
              ? `Last saved from ${latest.file_name || 'a PDF'}${latest.applied_at ? ` on ${formatDate(latest.applied_at)}` : ''}`
              : 'Upload their profile (More > Save to PDF on LinkedIn) to verify it and add what it says.'}
          </span>
        </div>
      )}

      {notice && phase === 'idle' && (
        <p className="pdf-upload__notice" role="status"><Icon name="check" /> {notice}</p>
      )}
      {error && (
        <p className="pdf-upload__error" role="alert"><Icon name="alert-triangle" /> {error}</p>
      )}

      {(phase === 'review' || phase === 'saving') && review && (
        <div className="pdf-upload__panel">
          <div className="pdf-upload__file">
            <Icon name="file-text" /> {upload?.file_name || 'LinkedIn PDF'}
          </div>
          <ProfilePdfComparison review={review} />
          {review.needs_confirmation && (
            <label className="pdf-upload__confirm">
              <input type="checkbox" checked={confirmed} onChange={(e) => setConfirmed(e.target.checked)} />
              I have checked: this PDF is {contactName || 'this person'}&rsquo;s profile
            </label>
          )}
          <div className="pdf-upload__actions">
            <button type="button" className="pdf-upload__save" onClick={save} disabled={!canSave || phase === 'saving'}>
              <Icon name={phase === 'saving' ? 'loader' : 'check'} spin={phase === 'saving'} />
              {phase === 'saving' ? 'Saving…' : 'Save to this person'}
            </button>
            <button type="button" className="pdf-upload__cancel" onClick={cancel} disabled={phase === 'saving'}>
              Cancel
            </button>
          </div>
        </div>
      )}
    </section>
  );
};

export default ProfilePdfUpload;
